// RAPIFAST :: Verificación administrativa de conductores (DEMO, estado compartido).
// Secciones A–G del Formulario de Registro y Verificación de Conductores v1.0.
// El admin aprueba/rechaza CADA documento con motivo; el estado fluye a "Mis documentos".
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Ban, Eye, Ban as BlockIcon } from "lucide-react";
import { PageHeader } from "@/components/kit";
import {
  DemoBanner, Badge, SemaforoBadge, Field, SectionCard, ConfidentialDocModal,
} from "@/components/verificationKit";
import { useVerificationDemo } from "@/context/VerificationDemoContext";
import {
  DOC_LABELS, DOC_STATUS, DOC_WITH_FILE, VERIF_STATUS, DRIVER_DECLARATIONS,
  driverRule, driverSemaforo, driverDisplayStatus, isExpired,
} from "@/lib/verificationMock";

const inputCls =
  "rounded-lg border border-[#333] bg-[#1F1F1F] px-2.5 py-1.5 text-xs text-gray-200 outline-none focus:border-[#FF6A00]";

function DocRow({ d, docKey, onSetDoc, onView }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const doc = d.docs[docKey];
  const expired = isExpired(doc);
  const uploaded = !["NOT_UPLOADED", "NOT_APPLICABLE"].includes(doc.status);
  const displayStatus = expired ? "REJECTED" : doc.status;

  const approve = () => {
    if (!uploaded) { toast.error("No hay documento adjunto para verificar"); return; }
    if (expired) { toast.error("El documento está vencido; el conductor debe recargarlo"); return; }
    onSetDoc(docKey, { status: "VERIFIED", notes: "" });
    toast.success(`${DOC_LABELS[docKey]} verificado (demo)`);
  };
  const confirmReject = () => {
    if (!reason.trim()) { toast.error("El motivo de rechazo es obligatorio"); return; }
    onSetDoc(docKey, { status: "REJECTED", notes: reason.trim() });
    setRejecting(false); setReason("");
    toast.success(`${DOC_LABELS[docKey]} rechazado con motivo (demo)`);
  };

  return (
    <div className="rounded-xl border border-[#1e1e21] bg-[#101012] px-3 py-2.5" data-testid={`doc-row-${docKey.toLowerCase()}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[160px] grow">
          <div className="text-sm font-semibold text-white">{DOC_LABELS[docKey]}</div>
          {doc.file?.fileName && <div className="text-xs text-gray-500">Archivo: {doc.file.fileName}</div>}
          {doc.notes && doc.status === "REJECTED" && <div className="text-xs text-red-400">Motivo: {doc.notes}</div>}
          {doc.expiresAt && (
            <div className={`text-xs ${expired ? "font-bold text-red-400" : "text-gray-500"}`}>
              Vence: {doc.expiresAt}{expired && " · VENCIDO"}
            </div>
          )}
        </div>
        <Badge tone={DOC_STATUS[displayStatus]} testId={`doc-status-${docKey.toLowerCase()}`}>
          {expired ? "Vencido" : DOC_STATUS[doc.status].label}
        </Badge>
        {["LICENCIA", "SOAT", "TECNOMECANICA"].includes(docKey) && (
          <input
            type="date"
            className={inputCls}
            value={doc.expiresAt || ""}
            onChange={(e) => onSetDoc(docKey, { expiresAt: e.target.value || null })}
            data-testid={`doc-${docKey.toLowerCase()}-expires-at-input`}
          />
        )}
        {DOC_WITH_FILE.includes(docKey) && doc.file && (
          <button onClick={() => onView(docKey)} data-testid={`document-view-signed-url-button-${docKey.toLowerCase()}`}
            className="flex items-center gap-1 rounded-lg bg-[#FF6A00]/15 px-2.5 py-1.5 text-xs font-bold text-[#FF6A00] hover:bg-[#FF6A00]/25">
            <Eye size={13} /> Ver documento
          </button>
        )}
        {uploaded && (
          <>
            <button onClick={approve} data-testid={`doc-approve-${docKey.toLowerCase()}`}
              className="flex items-center gap-1 rounded-lg bg-emerald-500/15 px-2.5 py-1.5 text-xs font-bold text-emerald-400 hover:bg-emerald-500/25">
              <Check size={13} /> Verificar
            </button>
            <button onClick={() => setRejecting((v) => !v)} data-testid={`doc-reject-${docKey.toLowerCase()}`}
              className="flex items-center gap-1 rounded-lg bg-red-500/10 px-2.5 py-1.5 text-xs font-bold text-red-400 hover:bg-red-500/20">
              <Ban size={13} /> Rechazar
            </button>
          </>
        )}
      </div>
      {rejecting && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            className={`${inputCls} grow`}
            placeholder="Motivo del rechazo (obligatorio)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid={`doc-reject-reason-${docKey.toLowerCase()}`}
          />
          <button onClick={confirmReject} data-testid={`doc-reject-confirm-${docKey.toLowerCase()}`}
            className="rounded-lg bg-red-500/20 px-3 py-1.5 text-xs font-bold text-red-300 hover:bg-red-500/30">
            Confirmar rechazo
          </button>
        </div>
      )}
    </div>
  );
}

function DriverDetail({ d, onPatch, onSetDoc, onClose }) {
  const [viewDoc, setViewDoc] = useState(null);
  const [notes, setNotes] = useState(d.reviewNotes || "");
  const rule = driverRule(d);
  const st = driverDisplayStatus(d);

  const approve = () => {
    if (!rule.ok) {
      toast.error(`No se puede habilitar: faltan verificados ${rule.missing.map((k) => DOC_LABELS[k]).join(", ")}`);
      return;
    }
    onPatch(d.id, { status: "APPROVED", reviewNotes: notes, reviewedBy: "Admin demo", reviewedAt: new Date().toISOString().slice(0, 10) });
    toast.success("Conductor aprobado y habilitado (demo)");
  };
  const reject = () => {
    if (!notes.trim()) { toast.error("El motivo de rechazo es obligatorio"); return; }
    onPatch(d.id, { status: "REJECTED", reviewNotes: notes, reviewedBy: "Admin demo", reviewedAt: new Date().toISOString().slice(0, 10) });
    toast.success("Conductor rechazado con motivo (demo)");
  };

  return (
    <div className="mt-3 space-y-4" data-testid={`driver-detail-${d.id}`}>
      {!rule.ok && (
        <div data-testid={`not-enabled-banner-${d.id}`}
          className="flex items-center gap-2 rounded-xl border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm font-bold text-red-400">
          <BlockIcon size={16} /> NO HABILITADO PARA RECIBIR ASIGNACIONES — faltan verificados: {rule.missing.map((k) => DOC_LABELS[k]).join(", ")}
        </div>
      )}

      <SectionCard title="A. Datos básicos del conductor" testId="driver-section-a">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Nombres y apellidos" value={d.name} />
          <Field label="Tipo de documento" value={d.docType} />
          <Field label="Número de documento" value={d.docNumber} sensitive testId="driver-doc-number" />
          <Field label="Celular" value={d.phone} />
          <Field label="Correo" value={d.email} />
          <Field label="Municipio / zona de operación" value={d.zone} />
          <Field label="Fecha de nacimiento (opcional · minimización)" value={d.birthDate} />
          <Field label="Contacto de emergencia (opcional · minimización)" value={d.emergencyContact} />
        </div>
      </SectionCard>

      <SectionCard title="B. Datos del vehículo" testId="driver-section-b">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Tipo de vehículo" value={d.vehicle.type} />
          <Field label="Placa" value={d.vehicle.plate} />
          <Field label="Marca / línea / modelo" value={d.vehicle.brandModel} />
          <Field label="Propietario" value={d.vehicle.ownerName} />
          <Field label="Capacidad / restricciones" value={d.vehicle.capacity} />
        </div>
      </SectionCard>

      <SectionCard title="C. Checklist documental y vigencias" testId="driver-section-c">
        <p className="mb-3 text-xs text-gray-500">
          Regla del piloto: Cédula + Licencia + SOAT + Tarjeta de propiedad verificados y vigentes.
          Tecnomecánica solo si el vehículo no es motocicleta. Adjuntar no equivale a verificar.
        </p>
        <div className="space-y-2">
          {Object.keys(DOC_LABELS).map((k) => (
            <DocRow key={k} d={d} docKey={k} onSetDoc={(dk, patch) => onSetDoc(d.id, dk, patch)} onView={setViewDoc} />
          ))}
        </div>
      </SectionCard>

      <SectionCard title="D. Datos para liquidaciones" testId="driver-section-d">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Método" value={d.payout.method} />
          <Field label="Titular" value={d.payout.holder} />
          <Field label="Dato de pago" value={d.payout.account} sensitive />
        </div>
        <p className="mt-2 text-xs font-semibold text-amber-400">Nunca solicitar ni guardar claves, PIN ni datos de tarjetas.</p>
      </SectionCard>

      <SectionCard title="E. Confirmaciones del conductor" testId="driver-section-e">
        <ul className="space-y-2">
          {DRIVER_DECLARATIONS.map((txt, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
              <span className={`mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full ${d.confirmations[i] ? "bg-emerald-400" : "bg-gray-600"}`} />
              {txt}
            </li>
          ))}
        </ul>
      </SectionCard>

      <SectionCard title="F. Protección de datos" testId="driver-section-f">
        <Field label="Referencia de aviso / autorización" value={d.privacyRef} />
      </SectionCard>

      <SectionCard title="G. Revisión interna y estado" testId="driver-section-g">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Badge tone={VERIF_STATUS[st]} testId={`driver-overall-${d.id}`}>{VERIF_STATUS[st].label}</Badge>
          <SemaforoBadge level={driverSemaforo(d)} testId={`driver-semaforo-detail-${d.id}`} />
          {d.reviewedAt && <span className="text-xs text-gray-500">Revisado por {d.reviewedBy} · {d.reviewedAt}</span>}
        </div>
        <textarea className={`${inputCls} w-full`} rows={3}
          placeholder="Observaciones administrativas / motivo de rechazo (obligatorio al rechazar)"
          value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="driver-reject-reason-textarea" />
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={approve} data-testid="driver-card-approve-button"
            className="flex items-center gap-1.5 rounded-xl bg-emerald-500/15 px-4 py-2 text-sm font-bold text-emerald-400 hover:bg-emerald-500/25">
            <Check size={15} /> Aprobar y habilitar
          </button>
          <button onClick={reject} data-testid="driver-card-reject-button"
            className="flex items-center gap-1.5 rounded-xl bg-red-500/10 px-4 py-2 text-sm font-bold text-red-400 hover:bg-red-500/20">
            <Ban size={15} /> Rechazar
          </button>
          <button onClick={onClose} data-testid="driver-detail-close"
            className="rounded-xl border border-[#333] px-4 py-2 text-sm font-bold text-gray-400 hover:bg-[#1F1F1F]">
            Cerrar detalle
          </button>
        </div>
      </SectionCard>

      {viewDoc && (
        <ConfidentialDocModal docLabel={DOC_LABELS[viewDoc]} ownerName={d.name} file={d.docs[viewDoc].file} onClose={() => setViewDoc(null)} />
      )}
    </div>
  );
}

export default function AdminVerificationDrivers() {
  const [params] = useSearchParams();
  const { drivers, updateDriver, setDriverDoc } = useVerificationDemo();
  const [filter, setFilter] = useState("ALL");
  const [openId, setOpenId] = useState(params.get("sel"));

  const list = useMemo(
    () => drivers.filter((d) => filter === "ALL" || driverDisplayStatus(d) === filter),
    [drivers, filter]
  );

  return (
    <div data-testid="admin-verification-drivers">
      <PageHeader
        title="Verificación de conductores"
        subtitle="Revisión documental y habilitación para recibir asignaciones"
        testId="admin-drivers-tab"
        action={
          <select className={inputCls} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="driver-status-filter-select">
            <option value="ALL">Todos los estados</option>
            {Object.entries(VERIF_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        }
      />
      <DemoBanner />
      <div className="space-y-3" data-testid="driver-verification-table">
        {list.map((d) => {
          const rule = driverRule(d);
          const st = driverDisplayStatus(d);
          const verified = Object.values(d.docs).filter((x) => x.status === "VERIFIED").length;
          const open = openId === d.id;
          return (
            <div key={d.id} className="rounded-2xl border border-[#27272A] bg-[#141416] p-4 shadow-lg" data-testid={`driver-card-${d.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-[200px] grow">
                  <div className="font-heading text-base font-bold text-white">{d.name}</div>
                  <div className="text-xs text-gray-500">
                    {d.vehicle.plate || "sin placa"} · {d.zone} · {verified}/{Object.keys(d.docs).length} docs verificados
                  </div>
                </div>
                <SemaforoBadge level={driverSemaforo(d)} testId={`driver-semaforo-${d.id}`} />
                <Badge tone={VERIF_STATUS[st]} testId={`driver-status-${d.id}`}>{VERIF_STATUS[st].label}</Badge>
                {!rule.ok && <span className="text-xs font-bold text-red-400" data-testid={`driver-blocked-${d.id}`}>Bloqueado para asignaciones</span>}
                <button onClick={() => setOpenId(open ? null : d.id)} data-testid={`driver-review-${d.id}`}
                  className="rounded-lg bg-[#FF6A00]/15 px-3 py-1.5 text-xs font-bold text-[#FF6A00] hover:bg-[#FF6A00]/25">
                  {open ? "Ocultar" : "Revisar"}
                </button>
              </div>
              {open && <DriverDetail d={d} onPatch={updateDriver} onSetDoc={setDriverDoc} onClose={() => setOpenId(null)} />}
            </div>
          );
        })}
        {list.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#27272A] py-10 text-center text-sm text-gray-500">
            No hay conductores con este estado
          </div>
        )}
      </div>
    </div>
  );
}
